import { useTranslation } from 'react-i18next';

import { ObjectiveLoadErrorMessage, ObjectiveSectionLoader } from './ObjectivePageStates';
import OwnerGroup from './OwnerGroup';

import type { OwnerGroupData } from '@/hooks/objectives/useObjectiveOwners';
import type { TranslationKey } from '@/i18n-key';
import type { User } from '@/types/directory/User';

import { EMPTY_STATE_TEXT } from '@/components/objectives/objectiveStyles';

interface PillarObjectivesPeopleProps {
    groups: OwnerGroupData[];
    employeesById: Map<string, User>;
    /** The list, its people or the attribute filter are still being read. */
    isLoading: boolean;
    hasFilterError: boolean;
    /** What an empty list means to this reader, decided by the page; none when the page has
     *  already said why. */
    emptyMessageKey?: TranslationKey;
}

/** The pillar's objectives grouped by owner, with the three states the list can be in besides:
 * still being read, failed, or narrowed to nobody. */
export default function PillarObjectivesPeople({
    groups,
    employeesById,
    isLoading,
    hasFilterError,
    emptyMessageKey,
}: PillarObjectivesPeopleProps) {
    const { t } = useTranslation();

    if (isLoading) {
        return <ObjectiveSectionLoader dataTestId={'pillar-objectives-people-loader'} />;
    }

    if (hasFilterError) {
        return <ObjectiveLoadErrorMessage />;
    }

    if (groups.length === 0) {
        return emptyMessageKey ? <p className={EMPTY_STATE_TEXT}>{t(emptyMessageKey)}</p> : null;
    }

    return (
        <div className={'flex flex-col gap-12'}>
            {groups.map((group) => (
                <OwnerGroup
                    key={group.ownerId ?? 'none'}
                    group={group}
                    employeesById={employeesById}
                />
            ))}
        </div>
    );
}
